import {
  MessageSquare,
  Inbox,
  Bot,
  KanbanSquare,
  Zap,
  Users,
  Building2,
} from "lucide-react";
import React, { useState } from "react";
import { PageLayout } from "../components";
import ProductOverview from "../components/product/ProductOverview";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { showToastError } from "../shared/alertService";
import { openVirtusCrmSSO } from "../shared/virtusCrmSso";

/**
 * Página comercial do Virtus Chat (CRM). O fluxo de SSO é preservado
 * 100% como já existia — `openVirtusCrmSSO()` e o loading/erro em torno
 * dela não foram alterados, só passaram a ser chamados a partir do CTA da
 * nova estrutura de apresentação do produto.
 */
const VirtusWhatsapp: React.FC = () => {
  const { t } = useTranslation();
  const { activeContext } = useActiveContext();
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await openVirtusCrmSSO(activeContext);
    } catch (err: any) {
      showToastError(err?.message || t("Erro ao abrir o Virtus Chat"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title={t("Virtus Chat")}
      subtitle={t("CRM de WhatsApp integrado ao seu atendimento.")}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
    >
      <ProductOverview
        icon={MessageSquare}
        productName={t("Virtus Chat")}
        tagline={t(
          "Centralize atendimento, funil de vendas e automações do WhatsApp em um único CRM.",
        )}
        description={t(
          "O Virtus Chat reúne inbox em tempo real, funil de negócios (Pipelines), automações de mensagem, disparos em massa e um agente de IA para atendimento — tudo em um CRM dedicado ao WhatsApp. O acesso é feito com o mesmo login do Social Manager, sem precisar entrar de novo.",
        )}
        ctaLabel={t("Abrir Virtus Chat")}
        onCta={handleOpen}
        ctaLoading={loading}
        benefitsTitle={t("Por que usar o Virtus Chat")}
        benefitsSubtitle={t("O que ele resolve no seu atendimento")}
        benefits={[
          {
            icon: Inbox,
            title: t("Atendimento centralizado"),
            description: t(
              "Reúne as conversas de WhatsApp em uma inbox única, em tempo real, em vez de espalhadas entre dispositivos.",
            ),
          },
          {
            icon: KanbanSquare,
            title: t("Funil de vendas"),
            description: t(
              "Organiza negócios em um funil (Pipelines) direto a partir das conversas do WhatsApp.",
            ),
          },
          {
            icon: Zap,
            title: t("Automação de mensagens"),
            description: t(
              "Configura fluxos automáticos e disparos em massa (Broadcasts) sem depender de envio manual.",
            ),
          },
        ]}
        featuresTitle={t("Principais recursos")}
        featuresSubtitle={t("O que você encontra dentro do Virtus Chat")}
        features={[
          {
            icon: Inbox,
            title: t("Inbox"),
            description: t(
              "Conversas em tempo real, com contatos e histórico organizados.",
            ),
          },
          {
            icon: KanbanSquare,
            title: t("Pipelines"),
            description: t(
              "Funil de negócios em formato Kanban, ligado diretamente às conversas.",
            ),
          },
          {
            icon: Zap,
            title: t("Automações & Broadcasts"),
            description: t(
              "Fluxos automáticos de mensagem e disparos em massa com acompanhamento de status.",
            ),
          },
          {
            icon: Bot,
            title: t("Agente de IA"),
            description: t(
              "Agente com base de conhecimento configurável para respostas automáticas no atendimento.",
            ),
          },
        ]}
        flowTitle={t("Como ele se encaixa no seu atendimento")}
        flowSubtitle={t("Do contato do cliente até a sua equipe")}
        flow={[
          { icon: Users, label: t("Cliente no WhatsApp") },
          { icon: MessageSquare, label: t("Virtus Chat") },
          { icon: Building2, label: t("Sua Equipe") },
        ]}
      />
    </PageLayout>
  );
};

export default VirtusWhatsapp;
